<template>
  <div class="loading">
    <p-progress-spinner v-if="isLoading" animationDuration=".8s" />

    <div v-else-if="errorMessage" class="loading__error p-message p-message-error">
      <div class="p-message-wrapper">
        <span class="p-message-icon pi pi-times-circle"></span>
        <div class="p-message-text">
          {{ errorMessage }}
        </div>
      </div>

      <p-button
        icon="pi pi-refresh"
        label="Retry"
        @click="loadApplicationData"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useCurrentUserStore } from '@/stores/current-user-store.ts';
import { useRootStore } from '@/stores/root';
import { ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { getApiErrorMessages } from '@/util/error-utils.ts';

const currentUserStore = useCurrentUserStore();
const rootStore = useRootStore();
const router = useRouter();
const route = useRoute();
const isLoading = ref(false);
const errorMessage = ref<string | null>(null);

async function loadApplicationData() {
  errorMessage.value = null;
  isLoading.value = true;

  try {
    const currentUser = await currentUserStore.ensureLoaded();

    if (!currentUser) {
      await router.push({ name: '401' });
      return;
    }

    await rootStore.loadReferenceData();

    const redirectPath = route.query.redirect as string | undefined;
    await (redirectPath
      ? router.push(redirectPath)
      : router.push({ name: 'home' }));
  } catch (error) {
    console.error('Error loading application data:', error);

    errorMessage.value = getApiErrorMessages(
      error,
      'Unable to load the application data. Please try again.'
    ).join(' ');
    isLoading.value = false;
  }
}

loadApplicationData();
</script>

<style lang="scss" scoped>
.loading {
  position: absolute;
  background: var(--c_white);
  display: flex;
  align-items: center;
  justify-content: center;
  inset: 0;
  z-index: 300;
}

.loading__error {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  margin: 0 0.625rem;
  max-width: 20rem;
}
</style>
